import type { CalloutItem, CalloutProps } from '@highereducation/hen-ui';

import type { InferenceCalloutConfig, RenderAdapterResult } from '../types';

type CalloutPhase = 'pending' | 'generating' | 'generated' | 'empty' | 'error';

type PhasedCalloutProps = CalloutProps & {
  readonly 'data-phase': CalloutPhase;
};

const PROSE_KEYS = ['text', 'body', 'narrative'] as const;
const FAILURES = new Set(['timeout', 'malformed-json', 'empty-body']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readLabel(config: InferenceCalloutConfig | undefined): string {
  const heading =
    typeof config?.heading === 'string' ? config.heading.trim() : '';
  // The region is named by this text. A blank heading would point the name
  // at an empty element.
  return heading !== '' ? heading : 'Callout';
}

function readItemLimit(
  config: InferenceCalloutConfig | undefined,
): number | undefined {
  const raw = config?.itemLimit;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return undefined;
  }
  return Math.max(0, Math.trunc(raw));
}

interface SourceParts {
  readonly status?: string;
  readonly failure?: string;
  readonly payload: unknown;
}

function sourceParts(input: unknown): SourceParts {
  if (!isRecord(input)) {
    return { payload: input };
  }
  const status = typeof input.status === 'string' ? input.status : undefined;
  const failure = typeof input.failure === 'string' ? input.failure : undefined;
  if (Object.prototype.hasOwnProperty.call(input, 'data')) {
    return { status, failure, payload: input.data };
  }
  const payload: Record<string, unknown> = {};
  const keys = Object.keys(input);
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index] ?? '';
    if (key === 'status' || key === 'failure') {
      continue;
    }
    payload[key] = input[key];
  }
  return { status, failure, payload };
}

type Prose =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'empty-body' }
  | { readonly kind: 'blank' }
  | { readonly kind: 'none' };

function readProse(record: Record<string, unknown>): Prose {
  let sawBlank = false;
  for (let index = 0; index < PROSE_KEYS.length; index += 1) {
    const key = PROSE_KEYS[index] ?? 'text';
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      continue;
    }
    const value = record[key];
    if (value === null) {
      return { kind: 'empty-body' };
    }
    if (typeof value !== 'string') {
      continue;
    }
    if (value === '') {
      return { kind: 'empty-body' };
    }
    if (value.trim() === '') {
      sawBlank = true;
      continue;
    }
    return { kind: 'text', text: value };
  }
  return sawBlank ? { kind: 'blank' } : { kind: 'none' };
}

function readItems(
  record: Record<string, unknown>,
  itemLimit: number | undefined,
): CalloutItem[] | 'none' | 'empty' {
  if (!Object.prototype.hasOwnProperty.call(record, 'items')) {
    return 'none';
  }
  const raw = record.items;
  if (!Array.isArray(raw)) {
    return 'none';
  }
  return itemsFromList(raw, itemLimit);
}

function itemsFromList(
  raw: readonly unknown[],
  itemLimit: number | undefined,
): CalloutItem[] | 'empty' {
  const items: CalloutItem[] = [];
  for (let index = 0; index < raw.length; index += 1) {
    const entry = raw[index];
    if (typeof entry === 'string') {
      if (entry.trim() !== '') {
        items.push(entry);
      }
      continue;
    }
    if (!isRecord(entry) || typeof entry.text !== 'string') {
      continue;
    }
    if (entry.text.trim() === '') {
      continue;
    }
    items.push({ text: entry.text });
  }
  const limited = itemLimit === undefined ? items : items.slice(0, itemLimit);
  if (limited.length === 0) {
    return 'empty';
  }
  return limited;
}

function loading(
  label: string,
  phase: 'pending' | 'generating',
): PhasedCalloutProps {
  return { label, state: 'loading', 'data-phase': phase };
}

function emptyCallout(
  label: string,
  emptyCopy: string | undefined,
): PhasedCalloutProps {
  if (emptyCopy === undefined) {
    return { label, state: 'empty', 'data-phase': 'empty' };
  }
  return {
    label,
    state: 'empty',
    footer: emptyCopy,
    'data-phase': 'empty',
  };
}

function errorCallout(
  label: string,
  errorCopy: string | undefined,
): PhasedCalloutProps {
  if (errorCopy === undefined) {
    return { label, state: 'error', 'data-phase': 'error' };
  }
  return {
    label,
    state: 'error',
    errorContent: errorCopy,
    'data-phase': 'error',
  };
}

function fromPayload(
  payload: unknown,
  label: string,
  emptyCopy: string | undefined,
  errorCopy: string | undefined,
  itemLimit: number | undefined,
): PhasedCalloutProps {
  if (payload === null || payload === '') {
    return errorCallout(label, errorCopy);
  }
  if (typeof payload === 'string') {
    if (payload.trim() === '') {
      return emptyCallout(label, emptyCopy);
    }
    return {
      label,
      state: 'ready',
      body: payload,
      'data-phase': 'generated',
    };
  }
  // The binding passes a list itself, without a wrapping `{ items }` object.
  if (Array.isArray(payload)) {
    if (payload.length === 0) {
      return emptyCallout(label, emptyCopy);
    }
    const items = itemsFromList(payload, itemLimit);
    if (items === 'empty') {
      return emptyCallout(label, emptyCopy);
    }
    return {
      label,
      state: 'ready',
      items,
      'data-phase': 'generated',
    };
  }
  if (!isRecord(payload)) {
    return errorCallout(label, errorCopy);
  }
  if (payload.error === true) {
    return errorCallout(label, errorCopy);
  }
  const prose = readProse(payload);
  if (prose.kind === 'empty-body') {
    return errorCallout(label, errorCopy);
  }
  const items = readItems(payload, itemLimit);
  if (Array.isArray(items)) {
    return {
      label,
      state: 'ready',
      items,
      'data-phase': 'generated',
    };
  }
  if (prose.kind === 'text') {
    return {
      label,
      state: 'ready',
      body: prose.text,
      'data-phase': 'generated',
    };
  }
  return emptyCallout(label, emptyCopy);
}

/**
 * Maps a settled source onto callout props.
 *
 * Five phases become the component's state. A payload with nothing to show
 * stays empty, including an empty list. A null payload, an empty prose
 * field, or a transport failure stays an error. The result never asks for
 * another attempt.
 */
export function inferenceCalloutAdapter(
  input: unknown,
  config?: InferenceCalloutConfig,
): RenderAdapterResult<PhasedCalloutProps> {
  const label = readLabel(config);
  const emptyCopy =
    typeof config?.emptyCopy === 'string' ? config.emptyCopy : undefined;
  const errorCopy =
    typeof config?.errorCopy === 'string' ? config.errorCopy : undefined;
  const itemLimit = readItemLimit(config);
  const source = sourceParts(input);

  if (source.failure !== undefined && FAILURES.has(source.failure)) {
    return { props: errorCallout(label, errorCopy) };
  }
  if (source.status === 'pending') {
    return { props: loading(label, 'pending') };
  }
  if (source.status === 'generating') {
    return { props: loading(label, 'generating') };
  }
  if (source.status === 'error') {
    return { props: errorCallout(label, errorCopy) };
  }
  if (source.status === 'empty') {
    return { props: emptyCallout(label, emptyCopy) };
  }

  return {
    props: fromPayload(source.payload, label, emptyCopy, errorCopy, itemLimit),
  };
}
